import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Address } from '@/components/Cards/AddressCard/Address/Address';
import { AffiliationAddress } from '@/components/Hcp/AffiliationAddress/AffiliationAddress';
import { useProfileMetaData } from '@/components/Hcp/HcpCard/_hooks/useProfileMetaData';
import { ProfileName } from '@/components/Hcp/ProfileName/ProfileName';
import { TEST_IDS } from '@/core/constants/app';
import type { Hcphco } from '@/core/graphql/generated/types';
import { useCopyToClipboard } from '@/utils/copyToClipboard';
import { formatLocalizedName } from '@/utils/formatters/formatLocalizedName';

import { ProfilePageHeader } from '../ProfilePageHeader/ProfilePageHeader';

import { hcpMetaData as sx } from './ProfileMetaData.styles';

type Props = {
	profile: Hcphco | null;
	loading?: boolean;
	withoutHeader?: boolean;
	withoutAddress?: boolean;
	withoutFooter?: boolean;
	withoutCountry?: boolean;
	withProfileLink?: boolean;
	withInactive?: boolean;
	withInternationalName?: boolean;
	withLink?: boolean;
	lineClamp?: number;
	inline?: boolean;
	withShortName?: boolean;
	withNickname?: boolean;
	withAffiliation?: boolean;
	chips?: ReactNode;
	customSx?: {
		root?: SxProps<Theme>;
		nameInfoContainer?: SxProps<Theme>;
		name?: SxProps<Theme>;
		nameRoot?: SxProps<Theme>;
	};
};

export const ProfileMetaData = ({
	profile,
	loading = false,
	withoutHeader = false,
	withoutAddress = false,
	withoutFooter = false,
	withoutCountry = false,
	withProfileLink = false,
	withInactive = false,
	withInternationalName = false,
	withLink = false,
	inline = false,
	lineClamp,
	withShortName = false,
	withNickname = false,
	withAffiliation = false,
	chips,
	customSx
}: Props) => {
	const { t } = useTranslation();
	const { copyToClipboard } = useCopyToClipboard();
	const { specialtyAndType, primaryAffiliation, primaryAddress } =
		useProfileMetaData(profile);

	const addressView = profile?.__typename === 'HCO' ? 'summary' : 'basic';
	const affiliationToShow =
		withAffiliation && profile?.__typename === 'HCP'
			? primaryAffiliation
			: null;

	const showHeader = !withoutHeader;
	const status = profile?.statusCda_V;
	const statusReason =
		profile?.__typename === 'HCP'
			? formatLocalizedName(profile.hcpReasonStatus)
			: undefined;
	const statusLine = [status, statusReason].filter(Boolean).join(' | ');

	const veevaIdNode = profile?.veevaid_V && (
		<Box sx={sx.veevaIdContainer} data-testid={TEST_IDS.profile.veevaId}>
			<Typography variant='body2' sx={sx.veevaIdLabel}>
				{t('Veeva ID')}: {profile.veevaid_V}
			</Typography>
			<IconButton
				size='small'
				onClick={() => copyToClipboard(profile.veevaid_V)}
				sx={sx.copyButton}
			>
				<ContentCopyIcon sx={sx.copyIcon} />
			</IconButton>
		</Box>
	);

	const nameBlock = (
		<ProfileName
			profile={profile}
			withDegrees
			chips={chips}
			shouldValidateMarkup={false}
			withProfileLink={withProfileLink}
			withInactive={withInactive}
			withInternationalName={withInternationalName}
			withLink={withLink}
			inline={inline}
			lineClamp={lineClamp}
			withShortName={withShortName}
			withNickname={withNickname}
			customSx={{
				name: [sx.name, customSx?.name ?? false] as SxProps<Theme>,
				root: customSx?.nameRoot
			}}
		/>
	);

	const leftDetails = (
		<>
			<Typography>{specialtyAndType}</Typography>

			{!withoutAddress &&
				(profile?.__typename === 'HCO' ? (
					primaryAddress && (
						<Address
							address={primaryAddress}
							view={addressView}
							inline
							withoutCountry={withoutCountry}
						/>
					)
				) : (
					<AffiliationAddress
						primaryAffiliation={affiliationToShow}
						addressView={addressView}
					/>
				))}
		</>
	);

	if (!showHeader) {
		return (
			<Box sx={[sx.root, customSx?.root ?? false] as SxProps<Theme>}>
				<Box
					sx={
						[
							sx.nameInfoContainer,
							customSx?.nameInfoContainer ?? false
						] as SxProps<Theme>
					}
				>
					{nameBlock}
					{leftDetails}
					{!withoutFooter && veevaIdNode}
				</Box>
			</Box>
		);
	}

	return (
		<Box sx={[sx.root, customSx?.root ?? false] as SxProps<Theme>}>
			<ProfilePageHeader profile={profile} loading={loading} />

			<Box
				sx={
					[
						sx.nameInfoContainer,
						customSx?.nameInfoContainer ?? false
					] as SxProps<Theme>
				}
			>
				{nameBlock}
			</Box>

			<Box sx={sx.bodyRow}>
				<Box sx={sx.rightColumn}>
					{statusLine && (
						<Typography variant='body2' sx={sx.statusLabel}>
							{statusLine}
						</Typography>
					)}
					{!withoutFooter && veevaIdNode}
				</Box>
				<Box sx={sx.leftColumn}>{leftDetails}</Box>
			</Box>
		</Box>
	);
};
